import { HttpClient, HttpContext } from '@angular/common/http'
import { inject, Injectable, NgZone, OnDestroy } from '@angular/core'
import { App } from '@capacitor/app'
import { Capacitor, PluginListenerHandle } from '@capacitor/core'
import { TranslateService } from '@ngx-translate/core'
import { Observable, of, Subscription } from 'rxjs'
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators'

import { environment } from '../../../environments/environment'
import { SKIP_AUTH_HEADER } from '../tokens/http-context.token'
import { UserData } from '../types/auth.type'
import { getTokenRemainingTimeMs, isTokenExpiringSoon } from '../utils/jwt.util'
import { mergeRenewedUserFields } from '../utils/user-data.util'
import { AuthService } from './auth.service'
import { HelperService } from './helper.service'

const API_URL = environment.apiUrl

@Injectable({
	providedIn: 'root',
})
export class TokenRenewalService implements OnDestroy {
	private readonly httpClient = inject(HttpClient)

	private readonly helperService = inject(HelperService)

	private readonly authService = inject(AuthService)

	private readonly translateService = inject(TranslateService)

	private readonly ngZone = inject(NgZone)

	private renewTimeoutId: ReturnType<typeof setTimeout> | null = null

	private renewalInProgress$: Observable<boolean> | null = null

	private readonly bufferSeconds = 120 // Renew 2 minutes before expiry

	private isInitialized = false

	private readonly subscription = new Subscription()

	private visibilityHandler?: () => void

	private focusHandler?: () => void

	private storageHandler?: (event: StorageEvent) => void

	private appStateListener?: PluginListenerHandle

	constructor() {
		this.init()
	}

	init(): void {
		if (this.isInitialized) return
		this.isInitialized = true

		// React to user data changes (login, manual refresh, logout)
		this.subscription.add(
			this.helperService.userDataChanged$.subscribe(() => {
				this.scheduleRenewalTimer()
			}),
		)

		// Setup Web and Capacitor native lifecycle listeners
		this.setupLifecycleListeners()

		// Initial schedule on service startup
		this.scheduleRenewalTimer()
	}

	private setupLifecycleListeners(): void {
		if (typeof window !== 'undefined' && typeof document !== 'undefined') {
			this.visibilityHandler = () => {
				if (document.visibilityState === 'visible') {
					this.checkAndRenewIfExpiring()
				}
			}

			this.focusHandler = () => {
				this.checkAndRenewIfExpiring()
			}

			this.storageHandler = (event: StorageEvent) => {
				if (event.key === 'app') {
					this.scheduleRenewalTimer()
				}
			}

			document.addEventListener('visibilitychange', this.visibilityHandler)
			window.addEventListener('focus', this.focusHandler)
			window.addEventListener('storage', this.storageHandler)
		}

		if (Capacitor.isPluginAvailable('App')) {
			App.addListener('appStateChange', (state) => {
				if (state.isActive) {
					this.checkAndRenewIfExpiring()
				}
			})
				.then((handle) => {
					this.appStateListener = handle
				})
				.catch(() => {
					// Capacitor listener error fallback
				})
		}
	}

	/**
	 * Evaluates current token and schedules background timer for renewal.
	 */
	scheduleRenewalTimer(): void {
		this.clearTimer()

		const userData = this.helperService.getUserData()
		if (!userData || this.authService.isDemoUser() || !userData?.access_token) {
			return
		}

		const remainingMs = getTokenRemainingTimeMs(userData.access_token)
		if (remainingMs === 0) {
			if (isTokenExpiringSoon(userData.access_token, this.bufferSeconds) && !this.renewalInProgress$) {
				this.renewToken().subscribe()
			}
			return
		}

		const bufferMs = this.bufferSeconds * 1000
		const delayMs = Math.max(0, remainingMs - bufferMs)

		this.ngZone.runOutsideAngular(() => {
			this.renewTimeoutId = setTimeout(() => {
				this.ngZone.run(() => {
					this.renewToken().subscribe()
				})
			}, delayMs)
		})
	}

	/**
	 * Checks if the active token is nearing expiration and triggers renewal if so.
	 */
	checkAndRenewIfExpiring(): void {
		const userData = this.helperService.getUserData()
		if (!userData || this.authService.isDemoUser() || !userData?.access_token) {
			return
		}

		if (isTokenExpiringSoon(userData.access_token, this.bufferSeconds) && !this.renewalInProgress$) {
			this.renewToken().subscribe()
		}
	}

	/**
	 * Performs silent token renewal with deduplication for concurrent callers.
	 */
	renewToken(): Observable<boolean> {
		if (this.renewalInProgress$) {
			return this.renewalInProgress$
		}

		const userData = this.helperService.getUserData()
		if (!userData?.access_token || this.authService.isDemoUser()) {
			return of(false)
		}

		this.renewalInProgress$ = this.httpClient
			.post<UserData>(
				`${API_URL}/auth/renew`,
				{},
				{
					withCredentials: true,
					context: new HttpContext().set(SKIP_AUTH_HEADER, false),
				},
			)
			.pipe(
				tap((response: UserData) => {
					if (response?.access_token) {
						const currentUserData = this.helperService.getUserData() ?? ({} as UserData)
						const rawUser = response.user_fields ?? (response as unknown as Record<string, unknown>)['user']
						const mergedUser = mergeRenewedUserFields(rawUser, currentUserData.user_fields)

						const updatedUserData: UserData = {
							...currentUserData,
							...response,
							access_token: response.access_token,
							token: response.access_token,
							refresh_token: response.refresh_token ?? currentUserData.refresh_token,
							user_fields: mergedUser,
							user: mergedUser,
						} as unknown as UserData

						this.helperService.setUserData(updatedUserData)
						sessionStorage.setItem('token', response.access_token)
						sessionStorage.setItem('token_timestamp', Date.now().toString())
						if (response.refresh_token) {
							sessionStorage.setItem('refresh_token', response.refresh_token)
						}

						const lang = mergedUser?.LANGUAGE || 'en'
						this.translateService.use(lang)
						this.helperService.setSelectedLanguage(lang)
						this.helperService.clearTranslateCookie()
					}
				}),
				map((response) => Boolean(response?.access_token)),
				catchError((error: { status?: number }) => {
					console.error('Silent token renewal failed:', error)
					if (error?.status && [401, 403].includes(error.status)) {
						this.authService.logOut()
					}
					return of(false)
				}),
				finalize(() => {
					this.renewalInProgress$ = null
				}),
				shareReplay(1),
			)

		return this.renewalInProgress$
	}

	clearTimer(): void {
		if (this.renewTimeoutId) {
			clearTimeout(this.renewTimeoutId)
			this.renewTimeoutId = null
		}
	}

	ngOnDestroy(): void {
		this.clearTimer()
		this.subscription.unsubscribe()

		if (typeof window !== 'undefined' && typeof document !== 'undefined') {
			if (this.visibilityHandler) {
				document.removeEventListener('visibilitychange', this.visibilityHandler)
			}
			if (this.focusHandler) {
				window.removeEventListener('focus', this.focusHandler)
			}
			if (this.storageHandler) {
				window.removeEventListener('storage', this.storageHandler)
			}
		}

		if (this.appStateListener) {
			this.appStateListener.remove()
		}
	}
}
